import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { REPORT_DETAIL_CARD_BORDER } from "../../views/Dashboard/Reports/components/ReportsCardChrome";
import {
  STRATEGY_PINE_BADGE_CLASS,
  type StrategyPineBadgeVariant,
} from "../../views/Dashboard/Strategies/strategyTablePineBadges";

type StrategyPineSectionHeaderBadge = {
  variant: StrategyPineBadgeVariant;
  label: string;
  title?: string;
  pineBadgeKind: string;
};

export const STRATEGY_PINE_SECTION_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";

export const STRATEGY_PINE_SECTION_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 dark:text-white";

/** Scrollable shadcn table shell for parse result grids inside narrow dialogs. */
export const STRATEGY_PINE_DETAIL_TABLE_SHELL_CLASS =
  "w-full min-w-0 max-w-full overflow-x-auto overflow-y-hidden rounded-[6px] border border-border bg-background";

export const STRATEGY_PINE_DETAIL_TABLE_CLASS = "w-max min-w-full";

type StrategyPineSectionCardProps = {
  "data-strategy-pine-card": "active" | "master";
  title: string;
  description: React.ReactNode;
  headerBadge?: StrategyPineSectionHeaderBadge | null;
  onViewDetails: () => void;
  viewDetailsLabel?: string;
  /** When false, hides the header action until compose finishes (e.g. active pine card). */
  showViewDetailsButton?: boolean;
  children?: React.ReactNode;
};

export function StrategyPineSectionCard({
  "data-strategy-pine-card": pineCardKind,
  title,
  description,
  headerBadge,
  onViewDetails,
  viewDetailsLabel = "View details",
  showViewDetailsButton = true,
  children,
}: StrategyPineSectionCardProps): React.ReactElement {
  return (
    <Card
      {...(pineCardKind === "active"
        ? { "data-strategy-active-pine-card": "" }
        : { "data-strategy-master-pine-card": "" })}
      data-slot="card"
      className="gap-0 rounded-md py-0 shadow-none ring-0"
      style={{ border: `1px solid ${REPORT_DETAIL_CARD_BORDER}` }}
    >
      <CardHeader className="grid-cols-1 gap-3 px-3 py-3 has-data-[slot=card-action]:grid-cols-1 sm:grid-cols-[1fr_auto] sm:items-start">
        <div className="flex min-w-0 flex-col gap-1 text-left">
          <CardTitle
            className={`${STRATEGY_PINE_SECTION_LABEL_CLASS} inline-flex flex-wrap items-center gap-x-2 gap-y-0 text-sm font-normal`}
          >
            <span className="min-w-0">{title}</span>
            {headerBadge ? (
              <Badge
                variant={headerBadge.variant}
                data-strategies-pine-badge={headerBadge.pineBadgeKind}
                className={STRATEGY_PINE_BADGE_CLASS}
                title={headerBadge.title}
              >
                {headerBadge.label}
              </Badge>
            ) : null}
          </CardTitle>
          <CardDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>{description}</CardDescription>
        </div>
        <CardAction className="col-start-1 row-start-auto w-full sm:col-start-2 sm:row-span-2 sm:row-start-1 sm:w-auto sm:justify-self-end">
          {showViewDetailsButton ? (
            <Button
              type="button"
              variant="outline-brand"
              size="sm"
              data-font="primary"
              data-strategy-pine-view-details=""
              className="h-9 w-full shrink-0 sm:w-auto"
              onClick={onViewDetails}
            >
              <span className="normal-case tracking-normal">{viewDetailsLabel}</span>
            </Button>
          ) : null}
        </CardAction>
      </CardHeader>
      {children ? (
        <CardContent className="border-t border-border px-3 pt-3 pb-3">{children}</CardContent>
      ) : null}
    </Card>
  );
}
